import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getErrorMessage } from '@/shared/api/api-error';

import {
  createBrand,
  createDenomination,
  deleteBrand,
  deleteDenomination,
  getBrandDetails,
  getDeletionCheck,
  getBrands,
  getCatalogueImpact,
  getBrandOrder,
  getDenominationOrder,
  getDenominationOrderByValue,
  saveBrandOrder,
  saveDenominationOrder,
  setAvailability,
  setBrandStatus,
  setDenominationStatus,
  updateBrand,
  updateDenomination,
} from './brands.api';
import type {
  BrandListParams,
  CatalogueImpactRequest,
  CreateBrandPayload,
  CreateDenominationPayload,
  DenominationPayload,
  DenominationSort,
  UpdateBrandPayload,
} from '../types/brand.types';
import { DEFAULT_DENOMINATION_SORT } from '../types/brand.types';

/**
 * Query keys for the brands feature.
 *
 * Every key starts with `['brands']`, so invalidating `brandKeys.all` after a
 * change refreshes every list and detail view at once (§7).
 *
 *   all              ['brands']
 *   lists()          ['brands', 'list']
 *   list(params)     ['brands', 'list', { page, search, countryId, … }]
 *   details()        ['brands', 'detail']
 *   detail(id)       ['brands', 'detail', 12]
 *   impact(id, req)  ['brands', 'impact', 12, { change, … }]
 *   order()          ['brands', 'order']                      storefront brand order
 *   denominationOrder(id, region)  ['brands', 'denomination-order', 12, 3]
 */
export const brandKeys = {
  all: ['brands'] as const,
  lists: () => [...brandKeys.all, 'list'] as const,
  list: (params: BrandListParams) => [...brandKeys.lists(), params] as const,
  details: () => [...brandKeys.all, 'detail'] as const,
  detail: (brandId: number) => [...brandKeys.details(), brandId] as const,
  impact: (brandId: number, request: CatalogueImpactRequest) =>
    [...brandKeys.all, 'impact', brandId, request] as const,
  deletionCheck: (brandId: number) => [...brandKeys.all, 'deletion-check', brandId] as const,
  order: () => [...brandKeys.all, 'order'] as const,
  denominationOrder: (brandId: number, regionId: number) =>
    [...brandKeys.all, 'denomination-order', brandId, regionId] as const,
};

/** Other features' root keys — kept literal to avoid importing those features. */
const CATEGORIES_KEY = ['categories'] as const;
const REGIONS_KEY = ['regions'] as const;

export function useBrands(params: BrandListParams) {
  return useQuery({
    queryKey: brandKeys.list(params),
    queryFn: ({ signal }) => getBrands(params, signal),
    // Keeps the previous page on screen while the next one loads.
    placeholderData: (previous) => previous,
  });
}

/** Brand Details for every country in the staff member's scope. */
export function useBrandDetails(
  brandId: number | undefined,
  denominationSort: DenominationSort = DEFAULT_DENOMINATION_SORT,
) {
  const isDefault =
    denominationSort.sort === DEFAULT_DENOMINATION_SORT.sort &&
    denominationSort.direction === DEFAULT_DENOMINATION_SORT.direction;
  return useQuery({
    // The default order shares the plain key with every other reader of the
    // brand; a sorted view gets its own key under it, so invalidating
    // `detail(id)` still refreshes both.
    queryKey: isDefault
      ? brandKeys.detail(brandId ?? 0)
      : ([...brandKeys.detail(brandId ?? 0), denominationSort] as const),
    queryFn: ({ signal }) =>
      getBrandDetails(brandId as number, undefined, signal, denominationSort),
    enabled: brandId !== undefined && Number.isFinite(brandId),
    // Keeps the page on screen while a re-sorted list loads.
    placeholderData: (previous) => previous,
    // Reopening the edit dialog without any change serves the cache instead of
    // refetching (and flashing the loading state). An edit invalidates the
    // query, which overrides this and forces a refetch with the new values.
    staleTime: 30_000,
  });
}

/**
 * What a change would do, shown in its confirmation. Fetched only while the
 * confirmation is open (`request` set), and never cached across openings:
 * the catalogue may have changed since.
 */
export function useCatalogueImpact(brandId: number, request: CatalogueImpactRequest | null) {
  return useQuery({
    queryKey: brandKeys.impact(brandId, request ?? { change: 'activateBrand' }),
    queryFn: ({ signal }) => getCatalogueImpact(brandId, request as CatalogueImpactRequest, signal),
    enabled: request !== null,
    staleTime: 0,
    gcTime: 0,
  });
}

function useInvalidateBrand() {
  const queryClient = useQueryClient();
  return (brandId?: number) => {
    void queryClient.invalidateQueries({ queryKey: brandKeys.lists() });
    if (brandId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: brandKeys.detail(brandId) });
    }
  };
}

export function useCreateBrand() {
  const invalidate = useInvalidateBrand();

  return useMutation({
    mutationFn: (payload: CreateBrandPayload) => createBrand(payload),
    onSuccess: (_id, payload) => {
      toast.success(`Brand "${payload.name}" created.`);
      invalidate();
    },
    // Field errors (e.g. a taken name) are mapped onto the form by the caller.
  });
}

export function useUpdateBrand() {
  const invalidate = useInvalidateBrand();

  return useMutation({
    mutationFn: ({ brandId, payload }: { brandId: number; payload: UpdateBrandPayload }) =>
      updateBrand(brandId, payload),
    onSuccess: (_result, { brandId, payload }) => {
      toast.success(`Brand "${payload.name}" updated.`);
      invalidate(brandId);
    },
  });
}

/**
 * No optimistic updates for status or availability: they change what
 * customers can buy, so the screen shows a change only once the server has
 * made (and audited) it (§7).
 */
export function useSetBrandStatus() {
  const invalidate = useInvalidateBrand();

  return useMutation({
    mutationFn: ({
      brandId,
      isActive,
      reason,
    }: {
      brandId: number;
      isActive: boolean;
      reason: string | null;
    }) => setBrandStatus(brandId, isActive, reason),
    onSuccess: (changed, { brandId, isActive }) => {
      toast.success(
        changed === 0 ? 'Nothing to change.' : isActive ? 'Brand activated.' : 'Brand deactivated.',
      );
      invalidate(brandId);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not change the brand status.'));
    },
  });
}

export function useSetDenominationStatus() {
  const invalidate = useInvalidateBrand();

  return useMutation({
    mutationFn: (input: {
      brandId: number;
      denominationIds: number[];
      isActive: boolean;
      reason: string | null;
    }) => setDenominationStatus(input.brandId, input.denominationIds, input.isActive, input.reason),
    onSuccess: (changed, { brandId, isActive }) => {
      toast.success(
        changed === 0
          ? 'Nothing to change.'
          : `${changed} denomination${changed === 1 ? '' : 's'} ${isActive ? 'activated' : 'deactivated'}.`,
      );
      invalidate(brandId);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not change the denominations.'));
    },
  });
}

export function useSetAvailability() {
  const invalidate = useInvalidateBrand();

  return useMutation({
    mutationFn: (input: {
      brandId: number;
      countryId: number;
      denominationIds: number[];
      offered: boolean;
      reason: string | null;
    }) =>
      setAvailability(
        input.brandId,
        input.countryId,
        input.denominationIds,
        input.offered,
        input.reason,
      ),
    onSuccess: (changed, { brandId, offered }) => {
      toast.success(
        changed === 0
          ? 'Nothing to change.'
          : `${changed} denomination${changed === 1 ? '' : 's'} ${offered ? 'offered' : 'withdrawn'}.`,
      );
      invalidate(brandId);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not change availability.'));
    },
  });
}

/** Whether the brand and its denominations can be deleted. Always refetched: an order can arrive any time. */
export function useDeletionCheck(brandId: number, enabled = true) {
  return useQuery({
    queryKey: brandKeys.deletionCheck(brandId),
    queryFn: ({ signal }) => getDeletionCheck(brandId, signal),
    enabled,
    staleTime: 0,
  });
}

/** Deletes an unused brand with its denominations. The API's refusal is shown as a toast. */
export function useDeleteBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { brandId: number; name: string; reason: string | null }) =>
      deleteBrand(input.brandId, input.reason),
    onSuccess: (_result, { brandId, name }) => {
      toast.success(`Brand "${name}" deleted.`);
      queryClient.removeQueries({ queryKey: brandKeys.detail(brandId) });
      void queryClient.invalidateQueries({ queryKey: brandKeys.all });
      // Category and region counts include this brand's denominations.
      void queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
      void queryClient.invalidateQueries({ queryKey: REGIONS_KEY });
    },
    onError: (error, { brandId }) => {
      toast.error(getErrorMessage(error, 'Could not delete the brand.'));
      void queryClient.invalidateQueries({ queryKey: brandKeys.deletionCheck(brandId) });
    },
  });
}

/** Deletes one unused denomination. The API's refusal is shown as a toast. */
export function useDeleteDenomination(brandId: number) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateBrand();

  return useMutation({
    mutationFn: (input: { denominationId: number; name: string; reason: string | null }) =>
      deleteDenomination(brandId, input.denominationId, input.reason),
    onSuccess: (_result, { name }) => {
      toast.success(`Denomination "${name}" deleted.`);
      invalidate(brandId);
      void queryClient.invalidateQueries({ queryKey: brandKeys.deletionCheck(brandId) });
      void queryClient.invalidateQueries({ queryKey: REGIONS_KEY });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not delete the denomination.'));
      void queryClient.invalidateQueries({ queryKey: brandKeys.deletionCheck(brandId) });
    },
  });
}

export function useCreateDenomination(brandId: number) {
  const invalidate = useInvalidateBrand();

  return useMutation({
    mutationFn: (payload: CreateDenominationPayload) => createDenomination(brandId, payload),
    onSuccess: (_id, payload) => {
      toast.success(`Denomination "${payload.name}" created.`);
      invalidate(brandId);
    },
    // Field errors (a name in use, a locked region) are mapped onto the form by the caller.
  });
}

export function useUpdateDenomination(brandId: number) {
  const invalidate = useInvalidateBrand();

  return useMutation({
    mutationFn: ({
      denominationId,
      payload,
    }: {
      denominationId: number;
      payload: DenominationPayload;
    }) => updateDenomination(brandId, denominationId, payload),
    onSuccess: (_result, { payload }) => {
      toast.success(`Denomination "${payload.name}" updated.`);
      invalidate(brandId);
    },
  });
}

/**
 * The storefront brand order, exactly as the backend returns it. Refetched
 * every time the dialog opens: another admin may have saved an order since.
 */
export function useBrandOrder(enabled: boolean) {
  return useQuery({
    queryKey: brandKeys.order(),
    queryFn: ({ signal }) => getBrandOrder(signal),
    enabled,
    staleTime: 0,
  });
}

/** Errors (a stale or incomplete list) are shown in the dialog by the caller. */
export function useSaveBrandOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandIds, reason }: { brandIds: number[]; reason: string }) =>
      saveBrandOrder(brandIds, reason),
    onSuccess: (changed) => {
      toast.success(
        changed === 0 ? 'The order was already saved.' : 'Storefront brand order saved.',
      );
      // The order, the list's position column and every brand header change.
      void queryClient.invalidateQueries({ queryKey: brandKeys.all });
    },
    // A 409 means a brand was added or removed meanwhile — reload the order.
    onError: () => void queryClient.invalidateQueries({ queryKey: brandKeys.order() }),
  });
}

/** A brand's denominations in one region, exactly as the backend orders them. */
export function useDenominationOrder(brandId: number, regionId: number) {
  return useQuery({
    queryKey: brandKeys.denominationOrder(brandId, regionId),
    queryFn: ({ signal }) => getDenominationOrder(brandId, regionId, signal),
    staleTime: 0,
  });
}

/**
 * Asks the backend for the by-value suggestion. A mutation, not a query: it is
 * fetched on demand and only replaces the unsaved order being edited.
 */
export function useSuggestDenominationOrderByValue(brandId: number, regionId: number) {
  return useMutation({
    mutationFn: () => getDenominationOrderByValue(brandId, regionId),
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not suggest an order.'));
    },
  });
}

export function useSaveDenominationOrder(brandId: number, regionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ denominationIds, reason }: { denominationIds: number[]; reason: string }) =>
      saveDenominationOrder(brandId, regionId, denominationIds, reason),
    onSuccess: (changed) => {
      toast.success(changed === 0 ? 'The order was already saved.' : 'Denomination order saved.');
      void queryClient.invalidateQueries({
        queryKey: brandKeys.denominationOrder(brandId, regionId),
      });
      // Brand Details lists denominations in storefront order.
      void queryClient.invalidateQueries({ queryKey: brandKeys.detail(brandId) });
    },
    onError: () =>
      void queryClient.invalidateQueries({
        queryKey: brandKeys.denominationOrder(brandId, regionId),
      }),
  });
}
